import SectionHeader from '../UISystem/SectionHeader';
import { COLORS } from '../../styles/UISystem';

/**
 * NearbyList — the Nearby tab's body.
 *
 * Two modes:
 *  - Searching: collapses to a flat list (no headers), identical to every other
 *    People tab — search short-circuits grouping.
 *  - Not searching: an optional top banner (share-location prompt / no-anchor
 *    prompt) followed by SectionHeader-grouped cards from useNearbyGroups.
 *
 * Card rendering is delegated via `renderCard` so the card prop wiring stays in
 * one place (the list page). The Stage-2 map sits where the share banner is.
 *
 * @param {Array} groups - [{ key, title, items: [{ connection, ... }] }]
 * @param {boolean} loading
 * @param {boolean} hasAnchor - viewer has a location to anchor "Near you"
 * @param {boolean} isLocationSharing
 * @param {string} searchQuery
 * @param {Array} searchResults - connections to show flat while searching
 * @param {function} renderCard - (connection) => ReactNode
 * @param {function} onShareLocation - open the share flow (toggle / sheet)
 * @param {function} onSetHometown - go set the hometown (profile screen)
 */
const containerStyle = {
  padding: '0 16px 20px 16px',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  boxSizing: 'border-box',
};

const Banner = ({ children }) => (
  <div
    style={{
      backgroundColor: COLORS.LIGHT_GRAY,
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '20px',
      textAlign: 'center',
    }}
  >
    {children}
  </div>
);

const ActionLink = ({ children, onClick }) => (
  <span
    onClick={onClick}
    style={{ color: COLORS.PRIMARY_BLUE, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
  >
    {children}
  </span>
);

const NearbyList = ({
  groups,
  loading,
  hasAnchor,
  isLocationSharing,
  searchQuery,
  searchResults,
  renderCard,
  onShareLocation,
  onSetHometown,
}) => {
  // Searching → flat list, identical to every other tab
  if (searchQuery) {
    return (
      <div style={containerStyle}>
        {searchResults.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#666' }}>No matches</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {searchResults.map(renderCard)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Top banner (Stage 2: the map lives here) — never while resolving:
          loading ≠ "no anchor", the enable-instructions must not flash at
          users who already have sharing on */}
      {!loading && !hasAnchor ? (
        <Banner>
          <div style={{ fontSize: '15px', color: COLORS.CHAT_TEXT_DARK, marginBottom: '10px' }}>
            See who&apos;s nearby
          </div>
          <div style={{ fontSize: '14px', color: '#6A6F7A', lineHeight: 1.4 }}>
            <ActionLink onClick={onSetHometown}>Set your hometown</ActionLink> or{' '}
            <ActionLink onClick={onShareLocation}>share location</ActionLink> to group friends by
            where they are.
          </div>
        </Banner>
      ) : (
        !isLocationSharing && (
          <Banner>
            <div style={{ fontSize: '14px', color: '#6A6F7A', lineHeight: 1.4 }}>
              <ActionLink onClick={onShareLocation}>Turn on sharing</ActionLink> to see
              friends&apos; live locations.
            </div>
          </Banner>
        )
      )}

      {/* Grouped sections */}
      {loading ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: '#666' }}>Loading...</div>
      ) : groups.length === 0 ? (
        hasAnchor && (
          <div
            style={{ padding: '24px 0', textAlign: 'center', color: '#6A6F7A', fontSize: '14px' }}
          >
            No one to place yet — friends appear here once they share a hometown or location.
          </div>
        )
      ) : (
        groups.map((group) => (
          <div key={group.key} style={{ marginBottom: '20px' }}>
            {/* Headerless groups (title null): the proximity band + the no-anchor flat list */}
            {group.title && <SectionHeader>{group.title}</SectionHeader>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {group.items.map((item) => renderCard(item.connection))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default NearbyList;
